<template>
    <section v-if="items.length">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader title="Speakers & Special Guests" prefix="Event!" :icon="Mic" :options="layoutOptions" v-model="mode" />

            <!-- Portrait -->
            <div v-if="mode === 'portrait'" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <SpeakerPortraitCard v-for="(s, i) in items" :key="i" :speaker="s" @click="open(s)" />
            </div>

            <!-- Profile -->
            <div v-else-if="mode === 'profile'" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <SpeakerProfileCard v-for="(s, i) in items" :key="i" :speaker="s" @click="open(s)" />
            </div>

            <!-- Showcase -->
            <div v-else-if="mode === 'showcase'" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <SpeakerShowcaseCard v-for="(s, i) in items" :key="i" :speaker="s" @click="open(s)" />
            </div>

            <!-- List -->
            <div v-else-if="mode === 'list'" class="rounded-2xl border border-app bg-card/70 backdrop-blur-2xl divide-y divide-app">
                <SpeakerListRow v-for="(s, i) in items" :key="i" :speaker="s" @click="open(s)" />
            </div>

            <!-- Card (default) -->
            <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <SpeakerCard v-for="(s, i) in items" :key="i" :speaker="s" @click="open(s)" />
            </div>
        </div>

        <EventSpeakerModal :speaker="selected" @close="selected = null" />
    </section>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { Mic, LayoutGrid, List, IdCard, UserRound, GalleryVertical } from 'lucide-vue-next';
import { EventSpeaker } from '~/types';
import EventSpeakerModal from './EventSpeakerModal.vue';
import SectionHeader from './SectionHeader.vue';
import SpeakerCard from './SpeakerCards/SpeakerCard.vue';
import SpeakerPortraitCard from './SpeakerCards/SpeakerPortraitCard.vue';
import SpeakerShowcaseCard from './SpeakerCards/SpeakerShowcaseCard.vue';
import SpeakerProfileCard from './SpeakerCards/SpeakerProfileCard.vue';
import SpeakerListRow from './SpeakerCards/SpeakerListRow.vue';

type SpeakerLayout = 'card' | 'portrait' | 'profile' | 'showcase' | 'list';

const props = withDefaults(
    defineProps<{ items: EventSpeaker[]; variant?: SpeakerLayout }>(),
    { variant: 'card' }
);

const mode = ref<SpeakerLayout>(props.variant);
const selected = ref<EventSpeaker | null>(null);
const open = (s: EventSpeaker) => {
    selected.value = s;
};

const layoutOptions: { value: SpeakerLayout; label: string; icon: any }[] = [
    { value: 'card', label: 'Cards', icon: LayoutGrid },
    { value: 'portrait', label: 'Portraits', icon: IdCard },
    { value: 'profile', label: 'Profiles', icon: UserRound },
    { value: 'showcase', label: 'Showcase', icon: GalleryVertical },
    { value: 'list', label: 'List', icon: List },
];
</script>

<style scoped>
.bg-card {
    background-color: rgb(var(--core-card-bg));
}

.border-app {
    border-color: rgb(var(--core-app-border));
}

.divide-app> :not([hidden])~ :not([hidden]) {
    border-color: rgb(var(--core-app-border));
}
</style>
